import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import {trackEvent} from 'analytics/Analytics';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS, RERUN_SOURCE} from 'constants/AnalyticsConstants';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigEditorPane from 'pipelines/generalized-step-config-editor/StepConfigEditorPane';
import {getDataFrameInfosFromInputDataFrames} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {stepIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {getInputStepDatasRecord, inputStepDatasAreComplete} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import {withLoadingScreen} from 'util/Promises';
import removeNulls from 'util/removeNulls';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from './RightSidebarSkeleton';

interface StepConfigContentsProps {
  step: PipelineStep;
  setConfig: (config: PipelineStep['config']) => void;
  clientStateId: string;
  disabled?: boolean;
}

function StepConfigContents({
  step,
  setConfig,
  clientStateId,
  disabled,
}: StepConfigContentsProps): JSX.Element {
  const dataFramesInfos = useMemo(
    () => getDataFrameInfosFromInputDataFrames(getInputStepDatasRecord(step, PipelineStepDataKind.DATASET)),
    [step]
  );

  // While `CHART_VIEW` steps are supported by the config editor, not all chart types are.
  if (!stepIsConfigEditorSupported(step) || step.stepType === PipelineStepType.CHART_VIEW) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text={`${STEP_TYPE_TO_USER_FACING_NAME[step.stepType]} steps cannot be configured from the sidepanel.`}
      />
    );
  }

  const inputsAreComplete = inputStepDatasAreComplete(step);

  return (
    <>
      {inputsAreComplete || (
        <div className="u-small-spacing-bottom">
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="This step cannot be configured, because its inputs are incomplete."
          />
        </div>
      )}
      <StepConfigEditorPane
        stepType={step.stepType}
        stepVersion={step.stepVersion}
        config={step.config}
        setConfig={setConfig}
        dataFramesInfos={dataFramesInfos}
        readOnly={disabled || !inputsAreComplete}
        clientStateId={clientStateId}
        stepId={step ? step.id : undefined}
      />
    </>
  );
}

/** @package */
export interface EditStepRightSidebarProps {
  pipeline: Omit<PipelineGraphQL, 'execution'>;
  step: PipelineStep;
  /**
   * Called after a user clicks "Cancel".
   *
   * Callers should use this callback to hide the sidebar.
   */
  onCancel: () => void;
  /**
   * Called after a step has been updated, via `PipelineDAO`.
   *
   * Callers may use this callback to refresh their GraphQL query and hide the sidebar.
   */
  onSave: () => void;
}

/**
 * Sidebar to re-configure a step.
 *
 * The step should should already exist, but `config` may be null.
 *
 * This component does not yet have a read-only mode; the sidebar should only be shown if the pipeline is
 * unarchived and editable, and the step is safe to re-run.
 *
 * This component should be generic to `Analysis` or `AnalysisTemplate`.
 */
export default function EditStepRightSidebar({
  pipeline,
  step: originalStep,
  onCancel,
  onSave,
}: EditStepRightSidebarProps): JSX.Element {
  // Store a whole step as state, rather than just `draftConfig`, so the draft config does not go out-of-sync
  // with `stepType` and `inputStepDatas` during async updates.
  const [draftStep, setDraftStep] = useState<PipelineStep>(originalStep);

  // Refresh if the step changes or the step's incoming edges change. We do not refresh whenever the input
  // data changes, to avoid wiping away the user's draft changes too frequently.
  useEffect(() => {
    if (originalStep.id !== draftStep.id || !isEqual(originalStep.incomingEdges, draftStep.incomingEdges)) {
      setDraftStep(originalStep);
    }
  }, [originalStep, draftStep.id, draftStep.incomingEdges]);

  const {isComplete, errors} = validatePipelineStepConfig(
    draftStep.stepType,
    draftStep.stepVersion,
    draftStep.config ?? {},
    getInputStepDatasRecord(draftStep)
  );

  const {canEdit, message: disabledMessage} = checkCanModifyStep(
    pipeline,
    originalStep.id,
    'step',
    'modifyStep'
  );

  const bannerMessages: Array<InlineBannerProps> = removeNulls([
    originalStep.statusCode === PipelineStepStatusCode.FAILED
      ? {bannerType: InlineBannerType.ERROR, text: originalStep.statusMessage || 'This step failed.'}
      : null,
    disabledMessage != null ? {bannerType: InlineBannerType.INFO, text: disabledMessage} : null,
  ]);

  return (
    <RightSidebarSkeleton
      title={STEP_TYPE_TO_USER_FACING_NAME[draftStep.stepType]}
      confirmationButtons={[
        {text: 'Cancel', onClick: onCancel, dataTestAttrsElement: 'cancelButton'},
        {
          text: 'Save',
          isPrimary: true,
          disabled:
            !canEdit ||
            // Disable 'Save' if the config is incomplete or hasn't changed. Still allow clicking 'Save' if
            // the config is complete but empty, in order to not block the user on steps which do not require
            // configuration.
            (isEqual(draftStep.config, originalStep.config) && !isEqual(draftStep.config ?? {}, {})) ||
            !isComplete ||
            errors.length > 0,
          onClick: async (): Promise<void> => {
            trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.TRIGGERED_STEP_RERUN, {
              rerun_source: RERUN_SOURCE.CONFIG,
              step_id: draftStep.id,
              step_type: draftStep.stepType,
              pipeline_id: pipeline.id,
            });
            await withLoadingScreen(
              PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
                stepsToAdd: [],
                stepsToUpdate: [
                  {
                    stepId: draftStep.id,
                    config: draftStep.config ?? {},
                    rerunAfterUpdate: true,
                  },
                ],
              })
            );
            onSave();
          },
          dataTestAttrsElement: 'saveButton',
        },
      ]}
      width={RightSidebarWidth.WIDE}
      useAbsolutePositioning={true}
      draftStep={draftStep}
      originalStep={originalStep}
      clientStateId={originalStep.id}
      dataTestAttrs={{
        component: 'EditStepRightSidebar',
        element: 'root',
      }}
    >
      {bannerMessages.length > 0 && (
        <div className={css(sidebarStyles.bannerStack)}>
          <InlineBannerStack messages={bannerMessages} />
        </div>
      )}
      <div className={css(sidebarStyles.stepConfigContents)}>
        <StepConfigContents
          step={draftStep}
          setConfig={(config) => {
            setDraftStep({...draftStep, config});
          }}
          disabled={!canEdit}
          clientStateId={draftStep.id}
        />
      </div>
    </RightSidebarSkeleton>
  );
}

/**
 * Analysis-specific wrapper around `EditStepRightSidebar`, for use with `AnalysisRightSidebarRouter`.
 */
export function AnalysisEditStepRightSidebar({
  analysis,
  selectedRightSidebarItem,
  onChangeSelectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.EDIT_STEP>): JSX.Element {
  const step = getStepFromAnalysis(analysis, selectedRightSidebarItem.stepId);
  return (
    <EditStepRightSidebar
      pipeline={analysis}
      step={step}
      onCancel={() => {
        onChangeSelectedRightSidebarItem(null);
      }}
      onSave={() => {
        onChangeSelectedRightSidebarItem(null);
      }}
    />
  );
}

const sidebarStyles = StyleSheet.create({
  bannerStack: {
    marginBottom: 16,
  },
  stepConfigContents: {
    marginBottom: 16,
  },
});
